import { useMemo } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';

/**
 * Stable-location shim. v6's useLocation already returns a stable object across renders within
 * the same transition, so this wrapper is largely a no-op — kept temporarily to avoid touching
 * call sites during the v5→v6 migration. Slated for deletion in the final cleanup PR.
 */
interface StableLocation<T> {
  pathname: string;
  search: string;
  hash: string;
  state: T;
}

const useStableLocation = <T = unknown>(): StableLocation<T> => {
  const { pathname, search, hash, state } = useLocation();

  return useMemo<StableLocation<T>>(
    () => ({
      pathname,
      search,
      hash,
      // v6's useLocation types state as `any`. Cast once here so callers keep
      // the v5-style generic-state ergonomics.
      state: state as T,
    }),
    [pathname, search, hash, state],
  );
};

export default useStableLocation;
